import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { connect, useSelector } from 'react-redux';
import Bugsnag from '../../../../utils/bugsnag';
import Icon from '../../../Icon';
import FlagIcon from '../../../../public/static/images/item/flag.svg';
import FullFulfilledFlagIcon from '../../../../public/static/images/item/fullfied-flag.svg';
import useToggle from '../../../../hooks/useToggle';
import { ReportList } from './ReportList';
import { ReportInputModal } from './ReportInputModal';
import {
  setError,
  setReplenishmentNotifications,
  toggleReplenishmentNotificationsModal
} from '../../../../actions';
import { createReportMutation } from '../../../../api/mutations';
import { companyNameQuery } from '../../../../api/queries';
import {
  InputModalState,
  NotificationsType,
  ReportSectionProps
} from './types';
import { ReportSectionContainer, ReportText } from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import { selectCurrentItemAuthModalOpen } from '../../../../store/selectors';
import { InformationPopup } from '../../../ui/InformationPopup';
import SuccessIcon from '../../../../public/static/images/item/success-reported.svg';

const mapDispatchToProps = dispatch => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState)),
  notificationsData: (notifications: NotificationsType) => {
    dispatch(
      setReplenishmentNotifications(
        notifications?.profile?.businessNotifications
      )
    );
  },
  showNotificationsModal: (isOpen: boolean) => {
    dispatch(toggleReplenishmentNotificationsModal(isOpen));
  }
});

const ReportSectionComponent: React.FC<ReportSectionProps> = ({
  itemId,
  isReported,
  setErrorUI,
  notificationsData,
  showNotificationsModal,
  setIsReportModalOpen,
  setAdIsReported
}) => {
  const { t } = useTranslation();
  const { isSmall } = useScreenSize();
  const isAuthModalOpen = useSelector(selectCurrentItemAuthModalOpen);

  const { data } = useQuery(companyNameQuery, {
    ssr: false,
    fetchPolicy: 'network-only'
  });

  const [createReport, { loading: createReportLoading }] =
    useMutation(createReportMutation);
  const [isOpenModal, toggleModal] = useToggle(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isOpenAlertModal, setOpenAlertModal] = useState(false);
  const [inputModalState, setModalState] = useState<InputModalState>({
    isOpen: false,
    reason: null,
    isSubmitted: isReported
  });

  useEffect(() => {
    setModalState(prevState => ({
      ...prevState,
      isSubmitted: isReported
    }));
  }, [isReported]);

  const phoneOrName = data?.profile?.phone || data?.profile?.name;

  const handleToggleModal = () => {
    if (!inputModalState.isSubmitted) {
      toggleModal();
    }
  };

  const handleToggleInputModal = (reason: { id: string; reason: string }) => {
    setModalState(prevState => ({
      ...prevState,
      isOpen: true,
      reason: { ...reason }
    }));
    toggleModal();
  };

  const handleInputModalSubmit = ({ report }) => {
    if (!inputModalState.reason?.id) return;

    createReport({
      variables: {
        itemId,
        reportReasonId: inputModalState.reason.id,
        description: report
      }
    })
      .then(({ data }) => {
        const errorMessage = data?.createReport?.errors?.[0]?.message;

        if (errorMessage) {
          setErrorMsg(errorMessage);
          return;
        }

        setModalState({ isOpen: false, reason: null, isSubmitted: true });
        setOpenAlertModal(true);
        setAdIsReported?.(true);
      })
      .catch(error => {
        setErrorUI(true);
        Bugsnag.notify(error);
      });
  };

  const handleCloseInputModal = () => {
    setErrorMsg('');
    setModalState(prevState => ({ ...prevState, isOpen: false, reason: null }));
  };

  const handleBackInputModal = () => {
    handleCloseInputModal();
    toggleModal();
  };

  const onReportModalClose = () => {
    setErrorMsg('');
    toggleModal();
  };

  useEffect(() => {
    if (data?.profile?.businessNotifications?.length) {
      notificationsData(data);
      showNotificationsModal(true);
    }
  }, [data]);

  useEffect(() => {
    if (setIsReportModalOpen) {
      setIsReportModalOpen(
        isOpenAlertModal ||
          isOpenModal ||
          isAuthModalOpen ||
          inputModalState.isOpen
      );
    }
  }, [isOpenAlertModal, isOpenModal, isAuthModalOpen, inputModalState.isOpen]);

  const iconSize = isSmall ? 30 : 35;

  const handleAlertClose = () => {
    setOpenAlertModal(false);
  };

  return (
    <>
      <ReportSectionContainer
        data-cy="report-section"
        role="button"
        tabIndex={0}
        onClick={handleToggleModal}
        onKeyPress={handleToggleModal}
        $isReported={inputModalState.isSubmitted}
        aria-label="Report this item"
      >
        <Icon
          indent={false}
          IconComponent={
            inputModalState.isSubmitted ? FullFulfilledFlagIcon : FlagIcon
          }
          height={iconSize}
          width={iconSize}
        />
        {isSmall && (
          <ReportText tag="body_1" $isReported={inputModalState.isSubmitted}>
            {inputModalState.isSubmitted
              ? t('pages.current_item.buttons.sent_complain')
              : t('pages.current_item.buttons.complain')}
          </ReportText>
        )}
      </ReportSectionContainer>

      {isOpenModal && (
        <ReportList
          onReportModalClose={onReportModalClose}
          onReportItemClickHandler={handleToggleInputModal}
          currentValue={inputModalState?.reason?.id}
        />
      )}

      {inputModalState.isOpen && (
        <ReportInputModal
          error={errorMsg}
          isAuthenticated={Boolean(phoneOrName)}
          isDisabledBtn={createReportLoading}
          onSubmitModal={handleInputModalSubmit}
          handleBack={handleBackInputModal}
          onCloseModal={handleCloseInputModal}
          title={inputModalState?.reason?.reason || ''}
          onInputChange={() => setErrorMsg('')}
        />
      )}

      <InformationPopup
        isOpen={isOpenAlertModal}
        modalId="report-alert"
        title={t('pages.current_item.alert_modal.accepted_complaint_title')}
        message={t('pages.current_item.alert_modal.accepted_complaint_message')}
        declineButtonText={t('pages.current_item.buttons.accepted')}
        onDeclineHandlerClick={handleAlertClose}
        icon={SuccessIcon}
        iconHeight={35}
        iconWidth={35}
      />
    </>
  );
};

export const ReportSection = connect(
  null,
  mapDispatchToProps
)(ReportSectionComponent);
